import type { ServiceAccount } from '@compass/client/types/service_account';
import { useRotateServiceAccountSecret } from '@hooks/useServiceAccounts';
import { getErrorMessage } from '@utils/errorMessages';
import { AlertCircle, AlertTriangle, Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { ShowServiceAccountSecretDialog } from '@/components/settings/ShowServiceAccountSecretDialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface RotateServiceAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serviceAccount: ServiceAccount;
}

export function RotateServiceAccountDialog({
  open,
  onOpenChange,
  serviceAccount,
}: RotateServiceAccountDialogProps) {
  const [confirmName, setConfirmName] = useState('');
  const [secret, setSecret] = useState<{ accessKeyId: string; secretKey: string } | null>(null);

  const rotateMutation = useRotateServiceAccountSecret();

  useEffect(() => {
    if (!open) {
      setConfirmName('');
      rotateMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const expectedName = serviceAccount.name ?? '';
  const nameMatches = confirmName.trim() === expectedName && expectedName.length > 0;

  const handleRotate = () => {
    if (!serviceAccount.id || !nameMatches) return;
    rotateMutation.mutate(
      { service_account_id: serviceAccount.id },
      {
        onSuccess: data => {
          if (data.service_account?.access_key_id && data.secret_key) {
            setSecret({
              accessKeyId: data.service_account.access_key_id,
              secretKey: data.secret_key,
            });
            onOpenChange(false);
          }
        },
      },
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Rotate Secret</DialogTitle>
            <DialogDescription>
              Generate a new secret for{' '}
              <span className="font-medium text-foreground">{expectedName}</span>. The previous
              secret stops working immediately.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="bg-attention-muted text-attention-foreground border border-attention-muted/50 px-4 py-3 rounded-md text-sm">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="font-medium">This is destructive</p>
                  <p className="mt-1">
                    Any automation using the old secret will get 401 on its next call. Make sure you
                    can update the new secret in your secret manager before rotating.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-name">
                Type <span className="font-mono font-medium text-foreground">{expectedName}</span>{' '}
                to confirm
              </Label>
              <Input
                id="confirm-name"
                value={confirmName}
                onChange={e => setConfirmName(e.target.value)}
                placeholder={expectedName}
                autoFocus
                disabled={rotateMutation.isPending}
              />
            </div>

            {rotateMutation.isError && (
              <div className="bg-destructive/10 border border-destructive/50 text-destructive px-4 py-3 rounded-md text-sm">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium">Failed to rotate</p>
                    <p className="mt-1">
                      {getErrorMessage(rotateMutation.error) || 'Please try again.'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={rotateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRotate}
              disabled={!nameMatches || rotateMutation.isPending}
            >
              {rotateMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Rotating...
                </>
              ) : (
                'Rotate Secret'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {secret && (
        <ShowServiceAccountSecretDialog
          open={secret !== null}
          onOpenChange={next => {
            if (!next) setSecret(null);
          }}
          accessKeyId={secret.accessKeyId}
          secretKey={secret.secretKey}
          title="Secret rotated"
          description="Copy the new secret now — it will not be shown again. The old secret no longer works."
        />
      )}
    </>
  );
}
